import type { MapComment } from '../types';

export type MapCommentDraftAnchor = Readonly<{
  anchorType: 'race_time' | 'map_point';
  anchorUtc: string | null;
  anchorLat: number;
  anchorLon: number;
}>;

export type MapCommentDraftPreview = GeoJSON.FeatureCollection<
  GeoJSON.Point,
  {
    anchorType: MapCommentDraftAnchor['anchorType'];
    isDraft: true;
    isEditing: true;
  }
>;

export type MapCommentPinFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Point,
  {
    commentId: number;
    hasAttachments: boolean;
    isEditing: boolean;
  }
>;
type MapCommentPinFeature = MapCommentPinFeatureCollection['features'][number];

const emptyPreview = (): MapCommentDraftPreview => ({
  type: 'FeatureCollection',
  features: [],
});

/** Builds the only on-map representation for each saved comment. */
export const buildMapCommentPinFeatureCollection = ({
  comments,
  editingCommentId = null,
}: {
  comments: readonly MapComment[];
  editingCommentId?: number | null;
}): MapCommentPinFeatureCollection => ({
  type: 'FeatureCollection',
  features: comments.flatMap<MapCommentPinFeature>((comment) => {
    const anchorLat = Number(comment.anchorLat);
    const anchorLon = Number(comment.anchorLon);
    if (
      !Number.isFinite(anchorLat) ||
      !Number.isFinite(anchorLon) ||
      Math.abs(anchorLat) > 90 ||
      Math.abs(anchorLon) > 180
    ) {
      return [];
    }
    return [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [anchorLon, anchorLat],
        },
        properties: {
          commentId: comment.id,
          hasAttachments: comment.attachments.length > 0,
          isEditing: comment.id === editingCommentId,
        },
      },
    ];
  }),
});

/**
 * Keeps edit preview in the same comment source as the saved pin. This avoids
 * a second marker that can become visually detached from the edited comment.
 */
export const applyMapCommentAnchorPreview = ({
  comments,
  commentId,
  anchor,
}: {
  comments: readonly MapComment[];
  commentId: number;
  anchor: MapCommentDraftAnchor;
}): MapComment[] =>
  comments.map((comment) =>
    comment.id === commentId
      ? {
          ...comment,
          anchorType: anchor.anchorType,
          anchorUtc: anchor.anchorUtc,
          anchorLat: anchor.anchorLat,
          anchorLon: anchor.anchorLon,
        }
      : comment,
  );

/**
 * Produces a single preview pin for a new, unsaved comment. It deliberately
 * stays hidden while editing because saved comments reuse their own pin.
 */
export const buildMapCommentDraftPreview = ({
  anchor,
  isComposerOpen,
  isEditing,
}: {
  anchor: MapCommentDraftAnchor | null;
  isComposerOpen: boolean;
  isEditing: boolean;
}): MapCommentDraftPreview => {
  if (
    !isComposerOpen ||
    isEditing ||
    !anchor ||
    !Number.isFinite(anchor.anchorLat) ||
    !Number.isFinite(anchor.anchorLon) ||
    Math.abs(anchor.anchorLat) > 90 ||
    Math.abs(anchor.anchorLon) > 180
  ) {
    return emptyPreview();
  }

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [anchor.anchorLon, anchor.anchorLat],
        },
        properties: {
          anchorType: anchor.anchorType,
          isDraft: true,
          isEditing: true,
        },
      },
    ],
  };
};
